import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import PaperDarkBG from "../kits/DesignKit/PaperDarkBG";
import UnderlineSketch from "./UnderlineSketch";
import MonoButton from "../kits/DesignKit/MonoButton";
import data from "../data/professionData.json";
import RoughBorder from "../kits/LayoutKit/RoughBorder";

// MediaSlot bleibt wie gehabt
function MediaSlot({ media, isHovered, isOpen, onToggle }) {
  const baseClasses = "transition-all duration-500 ease-in-out overflow-hidden";
  const heightClasses = isHovered ? "max-h-[180px] mt-4" : "max-h-0";

  if (media.type === "video") {
    return (
      <div className={`${baseClasses} ${heightClasses}`}>
        <div
          className="relative w-full h-[180px] cursor-pointer border border-black rounded-lg"
          onClick={onToggle}
        >
          <div className="absolute inset-0 border border-gray-600 rounded-xl skew-y-[0.25deg] translate-x-0.25 translate-y-0.25 z-0" />
          <div
            className={`absolute inset-0 w-full h-full rounded-lg transition-all duration-300 ${
              isOpen ? "backdrop-blur-none" : "backdrop-blur-sm"
            }`}
          >
            {isOpen ? (
              <iframe
                className="absolute inset-0 w-full h-full rounded-lg shadow-md"
                src={`https://www.youtube.com/embed/${media.id}?autoplay=1`}
                title="YouTube Video"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <>
                <img
                  className="w-full h-full object-cover rounded-lg shadow-md"
                  src={`https://img.youtube.com/vi/${media.id}/hqdefault.jpg`}
                  alt="Video Thumbnail"
                />
                <div className="absolute inset-0 bg-gray-50/50 backdrop-blur-sm rounded-lg" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-16 h-16 text-orange-300 transition-transform duration-500 hover:scale-110"
                  >
                    <path d="M5 3l14 9-14 9V3z" />
                  </svg>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (media.type === "image") {
    return (
      <div className={`${baseClasses} ${heightClasses}`}>
        <img
          src={media.url}
          alt={media.alt || "Bild"}
          loading="lazy"
          className="w-full h-[180px] object-cover rounded-lg border border-black"
        />
      </div>
    );
  }

  return null;
}

// Optimierte Card-Subkomponente mit min-w-[100px] Fix!
function ProblemCard({ item, idx, hovered, setHovered, openVideo, setOpenVideo }) {
  const cardRef = useRef();

  const media =
    item.media ??
    (item.videoId
      ? { type: "video", id: item.videoId }
      : item.imageId
      ? { type: "image", url: item.imageId }
      : null);

  return (
    <div
      className="relative transition-transform duration-300 ease-in-out"
      onMouseEnter={() => {
        if (hovered !== idx) setOpenVideo(null);
        setHovered(idx);
      }}
      onMouseLeave={() => setHovered(null)}
      onClick={() => setHovered(hovered === idx ? null : idx)}
    >
      <div
        ref={cardRef}
        className="bg-white text-gray-900 rounded-xl min-h-[260px] min-w-[100px] cursor-pointer relative shadow-md overflow-visible"
      >
        {/* *** WICHTIG: Nur der Content bekommt Padding! *** */}
        <div className="p-6">
          <h4 className="font-semibold text-black text-sm">
            Deine Herausforderung <UnderlineSketch />
          </h4>
          <div className="text-xl text-white bg-gray-950 border rounded-sm mb-2 px-1 pt-1 pb-1 flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm text-orange-300">{String(idx + 1).padStart(2, "0")}</span>
            <span className="text-white text-xs sm:text-sm">{item.title}</span>
          </div>
          <div className="mt-auto text-left ml-1">
            <div className="text-xs text-gray-900 mb-2 py-1">{item.problem}</div>
            <p className="text-xs text-gray-700">{item.challenge}</p>
          </div>

          {/* Lösung Overlay */}
          
       
          <div

            className={`
              absolute inset-0
              bg-gradient-to-br from-gray-50 via-orange-50 to-orange-50

              text-black p-6
              rounded-xl
              flex flex-col transition-all duration-500 ease-in-out
              ${hovered === idx ? "opacity-100 pointer-events-auto translate-y-0" : "opacity-0 pointer-events-none translate-y-full"}
            `}
            style={{ minHeight: "100%" }}
          >
            <h4 className="text-xs bg-gray-900 text-white px-2 pt-2 pb-2 rounded-md">
              Lösung <span className="text-orange-300 text-xs">{item.title}</span>
            </h4>
            <div className="py-2"></div>
            <p className="text-xs mb-4 flex-1">{item.solution}</p>
            {item.link && (
              <Link
                to={item.link}
                onClick={() => window.scrollTo(0, 0)}
                className="mt-auto w-full"
              >
                <MonoButton className="text-xs w-full">
                  Schau Dir meine Lösung an
                </MonoButton>
              </Link>
            )}
          </div>

          
        </div>
        {/* RoughBorder außen */}
        <RoughBorder
          parentRef={cardRef}
          radius={10}
          padding={1}
          color="#AC8E66" 
          strokeWidth={0.3}
          roughness={1}
          bowing={2}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 10,
            pointerEvents: "none"
          }}
        />
      </div>
      {/* Media-Slot */}
      {media && (
        <MediaSlot
          media={media}
          isHovered={hovered === idx}
          isOpen={openVideo === idx}
          onToggle={() =>
            setOpenVideo(openVideo === idx ? null : idx)
          }
        />
      )}
    </div>
  );
}

function ProfessionSection() {
  const [hovered, setHovered] = useState(null);
  const [openVideo, setOpenVideo] = useState(null);

  return (
    <PaperDarkBG>
      <section className="py-16 px-6 lg:px-12 text-white font-mono">
        <div className="max-w-screen-xl mx-auto">
          <h2 className="text-xl md:text-xl mb-12 text-gray-200 text-center">
            Echte <span className="text-orange-300 px-1 pb-1">Lösungen</span> für <br />
            <span>deine</span> <span className="px-1 text-orange-300">Herausforderungen</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {data.map((item, idx) => (
              <ProblemCard
                key={idx}
                item={item}
                idx={idx}
                hovered={hovered}
                setHovered={setHovered}
                openVideo={openVideo}
                setOpenVideo={setOpenVideo}
              />
            ))}
          </div>
        </div>
      </section>
    </PaperDarkBG>
  );
}

export default ProfessionSection;
